import { Link as RouterLink } from "react-router-dom";
// @mui
import { Icon } from "@iconify/react";
import { Box, Link, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
// routes
// components
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSubDistributor } from "src/api/distributor-api";
import { getSubDistributorGroup } from "src/api/sub-distributor-groups-api";
import Label from "src/components/label";
import logger from "src/utils/logger";
// Redux selectors - single source of truth for auth state
import {
  selectCurrentRole,
  selectUser,
  setDetailsVerified,
  setDetailsVerifiedValue,
} from "src/redux/authSlice";
import { PATH_DASHBOARD } from "../../../routes/paths";
import { CustomAvatar } from "../../../components/custom-avatar";

// ----------------------------------------------------------------------

const StyledRoot = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

// ----------------------------------------------------------------------

export default function NavAccount() {
  const dispatch = useDispatch();
  // Use Redux selectors - single source of truth for auth state
  const selectedRole = useSelector(selectCurrentRole);
  const user = useSelector(selectUser);

  const [subDistributor, setSubDistributor] = useState();
  const [group, setGroup] = useState();

  // Roles that require fetching sub-distributor details
  const SUB_DISTRIBUTOR_ROLES = [
    "sub-distributor",
    "agent",
    "sub_distributor_manager",
    "sub_distributor_finance",
    "sub_distributor_operations",
  ];

  useEffect(() => {
    if (!selectedRole) return;

    if (SUB_DISTRIBUTOR_ROLES.includes(selectedRole.role)) {
      async function fetchSubDistributor() {
        try {
          const response = await getSubDistributor(selectedRole.distributor_i_customer, selectedRole.sub_distributor_id);
          if (response.status_code === 1001) {
            const { data } = response;
            setSubDistributor(data);
            if (typeof data?.details_verified !== "undefined") {
              // If verified true, simple action; otherwise explicit value to sync
              if (data.details_verified) {
                if (!selectedRole.details_verified) dispatch(setDetailsVerified());
              } else if (selectedRole.details_verified !== false) dispatch(setDetailsVerifiedValue(false));
            }
          }
        } catch (error) {
          logger.error("Error fetching sub distributor", error);
        }
      }
      fetchSubDistributor();
    }
  }, [selectedRole, dispatch]);

  useEffect(() => {
    if (!selectedRole) return;

    if (selectedRole.role === "group_manager" && selectedRole.group_id) {
      async function fetchGroup() {
        try {
          const response = await getSubDistributorGroup(selectedRole.group_id);
          if (response.status_code === 1001) {
            const { data } = response;
            setGroup(data);
          }
        } catch (error) {
          logger.error("Error fetching group", error);
        }
      }
      fetchGroup();
    }
  }, [selectedRole]);

  // Don't render if not authenticated
  if (!selectedRole) {
    return null;
  }

  return (
    <Link component={RouterLink} to={PATH_DASHBOARD.user.account} underline="none" color="inherit">
      <StyledRoot
        sx={{
          padding: "16px 12px",
        }}
      >
        <CustomAvatar alt={selectedRole.customer_name} name="">
          <Icon icon="eva:person-fill" />
        </CustomAvatar>

        <Box sx={{ ml: 2, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {/* if user.type is admin show username */}
            {selectedRole.role === "admin" ? user?.username : selectedRole.customer_name}
          </Typography>
          {subDistributor && (
            <Typography variant="body2" noWrap sx={{ color: "text.secondary" }}>
              {subDistributor.name}
            </Typography>
          )}
          {group && (
            <Typography variant="body2" noWrap sx={{ color: "text.secondary" }}>
              {group.name}
            </Typography>
          )}
          {selectedRole.role !== "sub-distributor" && (
            <Label
              variant="soft"
              color={
                (selectedRole.role === "admin" && "primary") ||
                (selectedRole.role === "distributor" && "secondary") ||
                (selectedRole.role === "sub-distributor" && "success") ||
                (selectedRole.role === "support_agent" && "success") ||
                (selectedRole.role === "iot" && "info") ||
                (selectedRole.role === "agent" && "warning") ||
                (selectedRole.role === "group_manager" && "info") ||
                "info"
              }
              sx={{ mt: 1.5, fontSize: "0.7rem", my: 0 }}
            >
              {selectedRole.role === "support_agent"
                ? "Support Agent"
                : selectedRole.role === "finance"
                  ? "Finance"
                  : selectedRole.role === "sub_distributor_manager"
                    ? "Sub Distributor Manager"
                    : selectedRole.role === "sub_distributor_operations"
                      ? "Sub Distributor Operations"
                      : selectedRole.role === "sub_distributor_finance"
                        ? "Sub Distributor Finance"
                        : selectedRole.role === "group_manager"
                          ? "Group Manager"
                          : selectedRole.role}
            </Label>
          )}
        </Box>
      </StyledRoot>
    </Link>
  );
}
